import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import Loading from '../../components/Loading/Loading';

// Devang ID (always first)
const DEVANG_CHARACTER_ID = '695fba9ba25c1a76d7b317ad';

// Additional character IDs to always show (in order, after Devang)
const ADDITIONAL_CHARACTER_IDS = [
    DEVANG_CHARACTER_ID,
    '6947c0ef1ca003bf6c77842c',
    '6947bbfa1ca003bf6c77842a',
    '692edf151df0fa9cbab003e3',
    '692edeeb1df0fa9cbab003e2',
    '692edebc1df0fa9cbab003e1'
];

// Messages for characters
const CHARACTER_MESSAGES = {
    [DEVANG_CHARACTER_ID]: 'Main hoon Devang, tumhara career guide!',
    '6947c0ef1ca003bf6c77842c': 'Kaise ho? Batao kya chal raha hai...',
    '6947bbfa1ca003bf6c77842a': 'Aaj kuch naya seekhte hain!',
    '692edf151df0fa9cbab003e3': 'Main yahan hoon tumhari madad ke liye',
    '692edeeb1df0fa9cbab003e2': 'Chalo income badhane ki baat karte hain',
    '692edebc1df0fa9cbab003e1': 'Koi sawaal ho toh poochho!'
};

const EXCLUDED_CHAT_NAME_PATTERNS = [
    /gujarat\s+government\s+ai\s+expert/i,
];

const matchExcludedCharacter = (character) => {
    if (!character) return false;
    const candidateNames = [
        character?.name,
        character?.display_name,
        character?.alias,
        character?.displayMessage,
        character?.character?.name,
    ].filter(Boolean);

    if (candidateNames.length === 0) return false;

    const normalized = candidateNames.join(' ').toLowerCase();
    return EXCLUDED_CHAT_NAME_PATTERNS.some((pattern) => pattern.test(normalized));
};

const ChatHistory = ({ onCharacterSelect, allCharacters = [], userDetails = null }) => {
    const signupSource = userDetails?.signup_source;
    const utmMedium = signupSource?.utm_medium
        || new URLSearchParams(signupSource?.referrer || '').get('utm_medium');
    const isScannerUser = utmMedium === 'scanner';
    const [loading, setLoading] = useState(true);
    const [companions, setCompanions] = useState([]);
    const [inboxMetadata, setInboxMetadata] = useState([]);
    const [fetchError, setFetchError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const normalizeToArray = (value) => {
            if (Array.isArray(value)) return value;
            if (value && typeof value === 'object') return Object.values(value);
            return [];
        };

        const extractArray = (payload, keys) => {
            if (!payload) return [];
            for (const key of keys) {
                if (Object.prototype.hasOwnProperty.call(payload, key)) {
                    const candidate = payload[key];
                    if (candidate) return normalizeToArray(candidate);
                }
            }
            if (Array.isArray(payload)) return payload;
            if (payload && typeof payload === 'object') {
                const nestedArray = Object.values(payload).find(value => Array.isArray(value));
                if (nestedArray) return nestedArray;
            }
            return [];
        };

        const fetchCompanions = async () => {
            try {
                const response = await apiCall.get('/chat/companions');
                const payload = response?.data ?? response;
                const data = payload?.data ?? payload;
                const allowedCharacters = extractArray(data, [
                    'allowed_characters',
                    'allowedCharacters',
                    'characters',
                    'companions',
                    'character_list'
                ]);
                const inbox = extractArray(data, [
                    'inbox',
                    'inbox_metadata',
                    'inbox_entries',
                    'inboxMetadata',
                    'inboxData'
                ]);

                if (isMounted) {
                    setCompanions(allowedCharacters);
                    setInboxMetadata(inbox);
                }
            } catch (error) {
                console.error('Failed to fetch chat companions:', error);
                if (isMounted) {
                    setFetchError('Unable to load chats right now.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        void fetchCompanions();
        return () => {
            isMounted = false;
        };
    }, []);

    const resolveCharacterId = (item) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (Array.isArray(item)) return resolveCharacterId(item[0]);
        if (item.character) {
            const nestedId = resolveCharacterId(item.character);
            if (nestedId) return nestedId;
        }
        return (
            item._id ||
            item.id ||
            item.character_id ||
            item.characterId ||
            item.characterID ||
            null
        );
    };

    const normalizeCharacterSource = (source) => {
        if (!source) return null;
        if (source.character && typeof source.character === 'object') {
            return source.character;
        }
        return source;
    };

    const ensureObject = (value) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (value.character && typeof value.character === 'object') {
                return ensureObject(value.character);
            }
            return value;
        }
        return {};
    };

    const findCharacterById = (id) => {
        if (!id || !Array.isArray(allCharacters)) return null;
        return allCharacters.find((character) => {
            const candidateId = resolveCharacterId(character);
            return candidateId && String(candidateId) === String(id);
        });
    };

    const getInboxMessage = (entry) => {
        if (!entry) return null;
        return (
            entry.display_message ||
            entry.message ||
            entry.last_message ||
            entry.last_message_text ||
            entry.lastMessage?.text ||
            entry.lastMessage?.message ||
            entry.preview_message ||
            entry.previewText ||
            entry.snippet ||
            entry.metadata?.last_message ||
            entry.metadata?.preview ||
            null
        );
    };

    const getFallbackDisplayCharacters = () => {
        if (!Array.isArray(allCharacters) || allCharacters.length === 0) return [];

        const displayChars = [];
        ADDITIONAL_CHARACTER_IDS.forEach((id) => {
            const found = allCharacters.find(c =>
                String(resolveCharacterId(c)) === String(id)
            );
            if (found) {
                displayChars.push({
                    ...found,
                    displayMessage: CHARACTER_MESSAGES[id] || 'Tap to start chatting...'
                });
            }
        });
        return displayChars;
    };

    const getDisplayCharacters = () => {
        const allowedChars = Array.isArray(companions) ? companions : [];
        const inboxEntries = Array.isArray(inboxMetadata) ? inboxMetadata : [];

        if (allowedChars.length === 0 && inboxEntries.length === 0) {
            return getFallbackDisplayCharacters();
        }

        const displayMap = new Map();

        const addCharacterToDisplay = (characterSource, metadata) => {
            const normalizedSource = normalizeCharacterSource(characterSource);
            const sourceObject = ensureObject(normalizedSource);
            const metadataObject = ensureObject(metadata);
            const id =
                resolveCharacterId(sourceObject) ||
                resolveCharacterId(characterSource) ||
                resolveCharacterId(metadataObject);
            if (!id) return;

            const existing = displayMap.get(id);
            const fallbackDetails = existing || findCharacterById(id) || {};
            const enrichedSource = {
                ...fallbackDetails,
                ...sourceObject
            };

            const resolvedName =
                enrichedSource.name ||
                metadata?.character_name ||
                metadata?.name ||
                fallbackDetails?.name ||
                'Chat';

            const finalCharacter = {
                ...enrichedSource,
                _id: resolveCharacterId(enrichedSource) || id,
                id: resolveCharacterId(enrichedSource) || id,
                name: /scanneradda/i.test(resolvedName)
                    ? 'Scanner Expert'
                    : resolvedName,
                image_url: enrichedSource.image_url || enrichedSource.imageUrl || metadata?.image_url || metadata?.character_image_url || metadata?.avatar || fallbackDetails?.image_url || ''
            };

            const displayMessage = getInboxMessage(metadata) || existing?.displayMessage || CHARACTER_MESSAGES[id] || 'Tap to start chatting...';

            displayMap.set(id, {
                ...finalCharacter,
                displayMessage,
                inboxMetadata: metadata || existing?.inboxMetadata
            });
        };

        const findInboxEntryForCharacter = (character) => {
            if (!character) return null;
            const id = resolveCharacterId(character);
            if (!id) return null;

            return inboxEntries.find((entry) => {
                const entryId = resolveCharacterId(entry);
                return entryId && String(entryId) === String(id);
            });
        };

        allowedChars.forEach((character) => {
            const metadata = findInboxEntryForCharacter(character);
            addCharacterToDisplay(character, metadata);
        });

        inboxEntries.forEach((entry) => {
            const entryId = resolveCharacterId(entry);
            if (!entryId) return;
            if (displayMap.has(entryId)) {
                const existing = displayMap.get(entryId);
                const displayMessage = getInboxMessage(entry) || existing.displayMessage;
                displayMap.set(entryId, {
                    ...existing,
                    displayMessage,
                    inboxMetadata: entry
                });
                return;
            }
            const fallbackCharacter = findCharacterById(entryId) || { _id: entryId, name: entry.character_name || entry.name || 'Chat' };
            addCharacterToDisplay(fallbackCharacter, entry);
        });

        if (displayMap.size === 0) {
            return getFallbackDisplayCharacters();
        }

        return Array.from(displayMap.values());
    };

    const allDisplayCharacters = getDisplayCharacters();
    const filteredCharacters = allDisplayCharacters.filter((character) => !matchExcludedCharacter(character));
    
    // If user signed up via scanner, only show scanner expert
    const displayCharacters = isScannerUser
        ? filteredCharacters.filter(char => 
            /scanner/i.test(char.name) || 
            /scanneradda/i.test(char.name) ||
            /scanner.*expert/i.test(char.name)
          )
        : filteredCharacters;

    // Styles - Light Mode
    const styles = {
        container: {
            width: '100%',
            minHeight: '100%',
            backgroundColor: '#F0F7F0',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
        },
        header: {
            padding: '16px 16px 12px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.7)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            display: 'flex',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(73, 148, 62, 0.08)',
        },
        headerTitle: {
            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: '24px',
            fontWeight: '700',
            color: '#2D3B2D',
            margin: 0,
        },
        chatList: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#F0F7F0',
            flex: 1,
        },
        chatItem: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '14px 16px',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            cursor: 'pointer',
            borderBottom: '1px solid rgba(255, 255, 255, 0.6)',
            marginBottom: '1px',
            transition: 'all 0.2s ease',
        },
        avatar: {
            position: 'relative',
            width: '52px',
            height: '52px',
            flexShrink: 0,
            marginRight: '14px',
        },
        avatarImage: {
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            objectFit: 'cover',
            backgroundColor: '#E8F5E9',
            border: '2px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 12px rgba(73, 148, 62, 0.15)',
        },
        onlineDot: {
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '14px',
            height: '14px',
            backgroundColor: '#22C55E',
            border: '2.5px solid #FFFFFF',
            borderRadius: '50%',
        },
        chatContent: {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
        },
        chatName: {
            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: '17px',
            fontWeight: '600',
            color: '#2D3B2D',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            margin: 0,
        },
        chatMessage: {
            fontFamily: "'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: '15px',
            color: '#6B7280',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            margin: 0,
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderRadius: '20px',
            margin: '20px 16px',
            border: '1px solid rgba(255, 255, 255, 0.7)',
            boxShadow: '0 8px 32px rgba(73, 148, 62, 0.08)',
        },
        emptyText: {
            fontFamily: "'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: '16px',
            color: '#6B7280',
            margin: 0,
        },
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <Loading isLoading={true} />
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h2 style={styles.headerTitle}>Chats</h2>
            </div>

            {/* Chat List */}
            {displayCharacters.length === 0 ? (
                <div style={styles.emptyState}>
                    <p style={styles.emptyText}>{fetchError || 'No chats available'}</p>
                </div>
            ) : (
                <div style={styles.chatList}>
                    {displayCharacters.map((character, index) => (
                        <div
                            key={character._id || character.id || index}
                            style={styles.chatItem}
                            onClick={() => onCharacterSelect(character)}
                        >
                            <div style={styles.avatar}>
                                <img
                                    src={character.image_url}
                                    alt={character.name}
                                    style={styles.avatarImage}
                                />
                                <div style={styles.onlineDot}></div>
                            </div>
                            <div style={styles.chatContent}>
                                <p style={styles.chatName}>{character.name}</p>
                                <p style={styles.chatMessage}>{character.displayMessage}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ChatHistory;
